import { useState, useEffect } from 'react';
import { Clock, Play, Square, Users, Calendar, TrendingUp, ChevronLeft, ChevronRight, CalendarDays, Settings, Lock, Plus, Trash2, Edit2, X, Check, LogOut, Mail, Copy, UserPlus } from 'lucide-react';
import { authService } from './authService';
import { teamMemberService, timeEntryService, userService, organizationService } from './firebaseService';
import LandingPage from './src/LandingPage';

interface TeamMember {
  id: string;
  name: string;
  color: string;
  userId?: string;
  activeTimerStart?: number | null;
}

interface User {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: 'manager' | 'member';
  organizationId: string | null;
  teamMemberId: string | null;
}

interface Organization {
  id: string;
  name: string;
  createdBy: string;
  inviteCode: string;
}

interface TimeEntrySession {
  startTime: number;
  endTime: number;
  duration: number;
}

const UserAvatar = ({
  name,
  photoURL,
  size = 'w-8 h-8',
}: {
  name: string;
  photoURL?: string;
  size?: string;
}) => {
  const [imageError, setImageError] = useState(false);
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

  const showPhoto = Boolean(photoURL?.trim()) && !imageError;

  if (showPhoto) {
    return (
      <img
        src={photoURL}
        alt=""
        className={`${size} shrink-0 rounded-full object-cover`}
        onError={() => setImageError(true)}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div
      className={`${size} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-xs font-semibold text-white`}
      aria-hidden
    >
      {initials}
    </div>
  );
};

const TeamTimeTrackerFinal = () => {
  // Auth state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Organization state
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>([]);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showJoinTeam, setShowJoinTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  
  // App state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [currentSessionStart, setCurrentSessionStart] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timeEntries, setTimeEntries] = useState<Record<string, Record<string, TimeEntrySession[]>>>({});
  const [view, setView] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  
  // Navigation state for time periods
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Manager UI state
  const [showSettings, setShowSettings] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newEmployeeName, setNewEmployeeName] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const colors = [
    'from-blue-500 to-blue-600',
    'from-purple-500 to-purple-600',
    'from-green-500 to-green-600',
    'from-pink-500 to-pink-600',
    'from-cyan-500 to-cyan-600',
    'from-red-500 to-red-600',
    'from-yellow-500 to-yellow-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600',
    'from-emerald-500 to-emerald-600',
    'from-rose-500 to-rose-600',
    'from-violet-500 to-violet-600',
    'from-amber-500 to-amber-600',
    'from-lime-500 to-lime-600',
    'from-sky-500 to-sky-600'
  ];

  // Timer update effect
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (isTracking && currentSessionStart) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - currentSessionStart);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTracking, currentSessionStart]);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          let userProfile = await userService.getUser(firebaseUser.uid);
          
          if (!userProfile) {
            userProfile = await userService.createOrUpdateUser(firebaseUser.uid, {
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              photoURL: firebaseUser.photoURL || '',
              role: 'member',
              organizationId: null
            });
          } else {
            await userService.createOrUpdateUser(firebaseUser.uid, {
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              photoURL: firebaseUser.photoURL || ''
            });
          }
          
          setCurrentUser(userProfile as User);
          
          // Load all organizations user belongs to
          const orgs = await userService.getUserOrganizations(firebaseUser.uid);
          setUserOrganizations(orgs);
          
          // Set current org
          if (userProfile.organizationId && orgs.length > 0) {
            const currentOrganization = orgs.find(o => o.id === userProfile.organizationId);
            setCurrentOrg(currentOrganization || orgs[0]);
          } else if (orgs.length === 1) {
            setCurrentOrg(orgs[0]);
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      } else {
        setCurrentUser(null);
        setCurrentOrg(null);
        setUserOrganizations([]);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Helper function to assign unique colors
  const assignUniqueColors = (members: any[]) => {
    const usedColors = new Set<string>();
    return members.map((m, index) => {
      let assignedColor = m.color;
      
      // If no color or color already used, assign a unique one
      if (!assignedColor || usedColors.has(assignedColor)) {
        // Find first unused color from our color palette
        for (const color of colors) {
          if (!usedColors.has(color)) {
            assignedColor = color;
            break;
          }
        }
        // If all colors used, use index-based assignment
        if (usedColors.has(assignedColor)) {
          assignedColor = colors[index % colors.length];
        }
      }
      
      usedColors.add(assignedColor);
      return {
        id: String(m.id),
        name: m.name,
        color: assignedColor,
        userId: (m as any).userId
      };
    });
  };

  const isOrgManager = Boolean(
    currentUser && currentOrg && currentOrg.createdBy === currentUser.id
  );

  // Load team members (members only see their own profile)
  useEffect(() => {
    if (!currentUser || !currentOrg) return;

    const unsubscribe = teamMemberService.onTeamMembersChange(currentOrg.id, (members) => {
      const isManagerForOrg = currentOrg.createdBy === currentUser.id;
      const visibleMembers = isManagerForOrg
        ? members
        : members.filter((m) => m.userId === currentUser.id);

      const mappedMembers = assignUniqueColors(visibleMembers);
      setTeamMembers(mappedMembers);
      
      if (!isManagerForOrg) {
        // Find team member linked to this user
        const myProfile = mappedMembers.find(m => m.userId === currentUser.id);
        if (myProfile) {
          setSelectedMember(myProfile);
          
          // Check if this member has an active timer and resume it
          const firebaseMember = members.find(m => m.id === myProfile.id);
          if (firebaseMember?.activeTimerStart) {
            const savedStartTime = firebaseMember.activeTimerStart;
            const elapsedSoFar = Date.now() - savedStartTime;
            
            console.log('🔄 Resuming timer from Firebase:', {
              savedStartTime: new Date(savedStartTime),
              elapsedSoFar: Math.floor(elapsedSoFar / 1000) + ' seconds'
            });
            
            setIsTracking(true);
            setCurrentSessionStart(savedStartTime);
            setElapsedTime(elapsedSoFar);
          }
        }
      } else if (mappedMembers.length > 0 && !selectedMember) {
        // For managers, select first member by default
        setSelectedMember(mappedMembers[0]);
      }
    });

    return () => unsubscribe();
  }, [currentUser, currentOrg]);

  // Load time entries (members only load their own)
  useEffect(() => {
    if (!currentOrg || !currentUser || teamMembers.length === 0) return;

    const membersToLoad = isOrgManager
      ? teamMembers
      : teamMembers.filter((m) => m.userId === currentUser.id);

    if (membersToLoad.length === 0) return;

    setTimeEntries({});

    const unsubscribes: (() => void)[] = [];

    try {
      for (const member of membersToLoad) {
        const unsubscribe = timeEntryService.onTimeEntriesChange(
          currentOrg.id,
          member.id,
          (entries: any[]) => {
            console.log(`📊 Loading time entries for ${member.name}:`, entries);
            
            const entriesByDate: Record<string, TimeEntrySession[]> = {};
            entries.forEach(entry => {
              const date = entry.date;
              const startTime = parseEntryTimestamp(entry.startTime);
              const endTime = parseEntryTimestamp(entry.endTime);
              const duration = entry.duration ?? Math.max(0, endTime - startTime);

              if (!entriesByDate[date]) {
                entriesByDate[date] = [];
              }
              entriesByDate[date].push({ startTime, endTime, duration });
            });

            Object.values(entriesByDate).forEach((sessions) => {
              sessions.sort((a, b) => a.startTime - b.startTime);
            });
            
            setTimeEntries(prev => ({
              ...prev,
              [member.id]: entriesByDate
            }));
          }
        );
        
        unsubscribes.push(unsubscribe);
      }
    } catch (error) {
      console.error('Failed to load time entries:', error);
    }

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [currentOrg, currentUser, teamMembers, isOrgManager]);

  // Helper functions
  const parseEntryTimestamp = (value: unknown): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string') return new Date(value).getTime();
    if (typeof value === 'object' && value !== null) {
      const timestamp = value as { toDate?: () => Date; seconds?: number };
      if (typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
      if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
    }
    return new Date(String(value)).getTime();
  };

  const toLocalDateStr = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatClockTime = (ms: number) =>
    new Date(ms).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const formatSessionDuration = (ms: number) => {
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getSessionsForDate = (memberId: string, date: Date): TimeEntrySession[] => {
    const memberEntries = timeEntries[memberId] || {};
    return memberEntries[toLocalDateStr(date)] || [];
  };

  const calculateTotalTime = (memberId: string, dateRange: 'daily' | 'weekly' | 'monthly') => {
    const memberEntries = timeEntries[memberId] || {};
    let total = 0;

    Object.entries(memberEntries).forEach(([date, entries]) => {
      let shouldInclude = false;

      if (dateRange === 'daily') {
        // Compare date strings directly instead of converting to Date objects
        const todayString = currentDate.toISOString().split('T')[0];
        shouldInclude = date === todayString;
        console.log(`📅 Daily comparison: entryDate="${date}", todayString="${todayString}", shouldInclude=${shouldInclude}`);
      } else if (dateRange === 'weekly') {
        const entryDate = new Date(date);
        const weekStart = new Date(currentWeek);
        weekStart.setDate(currentWeek.getDate() - currentWeek.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        shouldInclude = entryDate >= weekStart && entryDate <= weekEnd;
      } else if (dateRange === 'monthly') {
        const entryDate = new Date(date);
        shouldInclude = entryDate.getMonth() === currentMonth.getMonth() && 
                       entryDate.getFullYear() === currentMonth.getFullYear();
      }

      if (shouldInclude) {
        total += entries.reduce((sum, session) => sum + session.duration, 0);
      }
    });

    return total;
  };

  const getTwoMonthTotal = (memberId: string) => {
    const memberEntries = timeEntries[memberId] || {};
    const now = new Date();
    let total = 0;

    Object.entries(memberEntries).forEach(([date, entries]) => {
      const entryDate = new Date(date);
      const daysDiff = Math.floor((now.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < 60) {
        total += entries.reduce((sum, session) => sum + session.duration, 0);
      }
    });

    return total;
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getTimeForDate = (memberId: string, date: Date) =>
    getSessionsForDate(memberId, date).reduce((sum, session) => sum + session.duration, 0);

  const changeMonth = (direction: number) => {
    setCalendarMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Date navigation helpers
  const navigateDate = (direction: number) => {
    if (view === 'daily') {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + direction);
      setCurrentDate(newDate);
    } else if (view === 'weekly') {
      const newWeek = new Date(currentWeek);
      newWeek.setDate(newWeek.getDate() + (direction * 7));
      setCurrentWeek(newWeek);
    } else if (view === 'monthly') {
      const newMonth = new Date(currentMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      setCurrentMonth(newMonth);
    }
  };

  const getCurrentPeriodLabel = () => {
    if (view === 'daily') {
      return currentDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } else if (view === 'weekly') {
      const weekStart = new Date(currentWeek);
      weekStart.setDate(currentWeek.getDate() - currentWeek.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (view === 'monthly') {
      return currentMonth.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long' 
      });
    }
    return '';
  };

  const resetToCurrentPeriod = () => {
    const now = new Date();
    setCurrentDate(now);
    setCurrentWeek(now);
    setCurrentMonth(now);
    setCalendarMonth(now);
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setCurrentOrg(null);
      setTeamMembers([]);
      setSelectedMember(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Team management
  const handleCreateTeam = async () => {
    if (!teamName.trim() || !currentUser) return;
    
    try {
      const org = await organizationService.createOrganization(teamName.trim(), currentUser.id);
      
      await userService.createOrUpdateUser(currentUser.id, {
        role: 'manager',
        organizationId: org.id
      });
      
      // Get next available color for manager
      const usedColors = new Set(teamMembers.map(m => m.color));
      let assignedColor = colors.find(c => !usedColors.has(c)) || colors[0];
      
      const teamMember = await teamMemberService.addTeamMember(
        org.id,
        currentUser.displayName,
        assignedColor,
        currentUser.id
      );
      
      await userService.linkUserToTeamMember(currentUser.id, teamMember.id, org.id);
      
      const updatedOrgs = await userService.getUserOrganizations(currentUser.id);
      setUserOrganizations(updatedOrgs);
      setCurrentOrg(org);
      setCurrentUser({
        ...currentUser,
        role: 'manager',
        organizationId: org.id,
        teamMemberId: teamMember.id
      });
      
      setShowCreateTeam(false);
      setTeamName('');
    } catch (error: any) {
      console.error('Error creating team:', error);
      alert(`Failed to create team: ${error.message || 'Unknown error'}`);
    }
  };

  const handleJoinTeam = async () => {
    if (!joinCode.trim() || !currentUser) return;
    
    try {
      const org = await organizationService.getOrganizationByInviteCode(joinCode.trim());
      
      if (!org) {
        alert('Invalid invite code');
        return;
      }
      
      await userService.createOrUpdateUser(currentUser.id, {
        role: 'member',
        organizationId: org.id
      });
      
      // Get next available color for member
      const usedColors = new Set(teamMembers.map(m => m.color));
      let assignedColor = colors.find(c => !usedColors.has(c)) || colors[1];
      
      const teamMember = await teamMemberService.addTeamMember(
        org.id,
        currentUser.displayName,
        assignedColor,
        currentUser.id
      );
      
      await userService.linkUserToTeamMember(currentUser.id, teamMember.id, org.id);
      
      const updatedOrgs = await userService.getUserOrganizations(currentUser.id);
      setUserOrganizations(updatedOrgs);
      setCurrentOrg(org);
      setCurrentUser({
        ...currentUser,
        role: 'member',
        organizationId: org.id,
        teamMemberId: teamMember.id
      });
      
      setShowJoinTeam(false);
      setJoinCode('');
    } catch (error: any) {
      console.error('Error joining team:', error);
      alert(`Failed to join team: ${error.message || 'Unknown error'}`);
    }
  };

  const switchTeam = async (org: Organization) => {
    if (!currentUser) return;
    
    // If there's an active timer, stop it before switching
    if (isTracking && selectedMember && currentSessionStart) {
      await stopTracking();
    }
    
    await userService.createOrUpdateUser(currentUser.id, {
      organizationId: org.id
    });
    
    setCurrentOrg(org);
    setShowTeamSelector(false);
    setIsTracking(false);
    setCurrentSessionStart(null);
    setElapsedTime(0);
  };

  const backToTeams = async () => {
    // If there's an active timer, stop it before going back
    if (isTracking && selectedMember && currentSessionStart) {
      await stopTracking();
    }
    
    setShowTeamSelector(true);
    setCurrentOrg(null);
    setIsTracking(false);
    setCurrentSessionStart(null);
    setElapsedTime(0);
  };

  const handleDeleteTeam = async (event: React.MouseEvent, orgId: string, orgName: string) => {
    event.stopPropagation(); // Prevent card click event
    
    if (!currentUser) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete the team "${orgName}"?\n\nThis will permanently delete:\n- All team members\n- All time entries\n- All team data\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    try {
      await organizationService.deleteOrganization(orgId);
      
      // If the deleted team was the current team, clear it
      if (currentOrg?.id === orgId) {
        setCurrentOrg(null);
        setTeamMembers([]);
        setSelectedMember(null);
      }
      
      // Reload user's organizations
      const orgs = await userService.getUserOrganizations(currentUser.id);
      setUserOrganizations(orgs);
      
      alert(`Team "${orgName}" has been deleted successfully.`);
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('Failed to delete team. Please try again.');
    }
  };

  const copyInviteCode = () => {
    if (currentOrg) {
      navigator.clipboard.writeText(currentOrg.inviteCode);
      alert('Invite code copied to clipboard!');
    }
  };

  // Time tracking (Member only)
  const startTracking = async () => {
    if (!selectedMember) return;
    
    const startTime = Date.now();
    setIsTracking(true);
    setCurrentSessionStart(startTime);
    setElapsedTime(0);
    
    // Save timer start to Firebase so it persists across page reloads
    try {
      await teamMemberService.startTimer(selectedMember.id);
      console.log('✅ Timer started and saved to Firebase');
    } catch (error) {
      console.error('Failed to save timer start:', error);
    }
  };

  const stopTracking = async () => {
    if (!currentSessionStart || !selectedMember || !currentOrg) return;
    
    const endTime = Date.now();
    const duration = endTime - currentSessionStart;
    const todayDate = new Date().toISOString().split('T')[0];
    
    console.log('🛑 Stopping timer:', {
      member: selectedMember.name,
      startTime: new Date(currentSessionStart),
      endTime: new Date(endTime),
      duration: duration,
      todayDate: todayDate,
      currentSessionStart: currentSessionStart
    });
    
    try {
      const savedEntry = await timeEntryService.addTimeEntry(
        currentOrg.id,
        selectedMember.id,
        currentSessionStart,
        endTime,
        ''
      );
      
      console.log('✅ Time entry saved:', savedEntry);
      
      // Clear timer state from Firebase
      await teamMemberService.stopTimer(selectedMember.id);
      console.log('✅ Timer cleared from Firebase');
      
      // Also update local state immediately for responsiveness
      setTimeEntries(prev => {
        const memberEntries = prev[selectedMember.id] || {};
        const dateEntries = memberEntries[todayDate] || [];
        
        const newEntries = {
          ...prev,
          [selectedMember.id]: {
            ...memberEntries,
            [todayDate]: [
              ...dateEntries,
              {
                startTime: currentSessionStart,
                endTime,
                duration,
              },
            ],
          }
        };
        
        console.log('🔄 Updated local timeEntries:', newEntries);
        return newEntries;
      });
      
      setIsTracking(false);
      setCurrentSessionStart(null);
      setElapsedTime(0);
    } catch (error) {
      console.error('❌ Error saving time entry:', error);
      alert('Failed to save time entry');
    }
  };

  // Manager functions
  const handleLogin = () => {
    if (password === 'BurhanER') {
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert('Incorrect password!');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowSettings(false);
  };

  const addEmployee = async () => {
    if (!newEmployeeName.trim() || !currentOrg) return;
    
    try {
      // Get next available color for new employee
      const usedColors = new Set(teamMembers.map(m => m.color));
      let assignedColor = colors.find(c => !usedColors.has(c)) || colors[teamMembers.length % colors.length];
      
      await teamMemberService.addTeamMember(currentOrg.id, newEmployeeName.trim(), assignedColor);
      setNewEmployeeName('');
    } catch (error) {
      console.error('Error adding team member:', error);
      alert('Failed to add team member');
    }
  };

  const deleteEmployee = async (id: string) => {
    if (!currentOrg) return;
    
    if (teamMembers.length <= 1) {
      alert('You must have at least one employee!');
      return;
    }
    
    const confirmed = window.confirm('Are you sure you want to delete this employee? All their time entries will be lost.');
    if (!confirmed) return;
    
    try {
      await teamMemberService.deleteTeamMember(id);
      if (selectedMember?.id === id) {
        setSelectedMember(teamMembers[0] || null);
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Failed to delete team member');
    }
  };

  const startEditing = (member: TeamMember) => {
    setEditingId(member.id);
    setEditingName(member.name);
  };

  const saveEdit = async () => {
    if (!editingName.trim() || !editingId) return;
    
    try {
      await teamMemberService.updateTeamMember(editingId, { name: editingName.trim() });
      setEditingId(null);
      setEditingName('');
    } catch (error) {
      console.error('Error updating team member:', error);
      alert('Failed to update team member');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  // Loading screen
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!currentUser) {
    return <LandingPage />;
  }

  // Team selection screen
  if (!currentOrg || showTeamSelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-6xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Team Time Tracker</h1>
                <p className="text-gray-300 text-sm">Welcome, {currentUser.displayName}!</p>
              </div>
            </div>
            
            <button
              onClick={handleSignOut}
              className="bg-red-500/20 hover:bg-red-500/30 text-red-300 px-4 py-2 rounded-lg flex items-center gap-2 transition-all border border-red-500/30"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>

          {/* My Teams Section */}
          {userOrganizations.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">My Teams</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userOrganizations.map(org => {
                  const isCreator = org.createdBy === currentUser.id;
                  return (
                    <div
                      key={org.id}
                      className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all group relative"
                    >
                      <button
                        onClick={() => switchTeam(org)}
                        className="w-full text-left"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                            <Users className="w-6 h-6 text-white" />
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-1">{org.name}</h3>
                        <p className="text-gray-400 text-sm">Code: {org.inviteCode}</p>
                        {isCreator && (
                          <p className="text-green-400 text-xs mt-2">👑 Manager</p>
                        )}
                      </button>
                      
                      {/* Delete button - only show for teams the user created, positioned at bottom right */}
                      {isCreator && (
                        <button
                          onClick={(e) => handleDeleteTeam(e, org.id, org.name)}
                          className="absolute bottom-4 right-4 p-2 bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded-lg transition-all border border-red-500/30 opacity-0 group-hover:opacity-100"
                          title="Delete team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Create or Join Team Section */}
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-white mb-4">
              {userOrganizations.length > 0 ? 'Add Another Team' : 'Get Started'}
            </h2>
          </div>

          {/* Main Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Create Team */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full mb-4">
                  <Plus className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Create a Team</h2>
                <p className="text-gray-300">Start tracking time with your team</p>
              </div>
              
              {showCreateTeam ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter team name..."
                    className="w-full bg-white/10 text-white placeholder-gray-400 px-4 py-3 rounded-lg border border-white/20 focus:outline-none focus:border-green-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleCreateTeam()}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateTeam}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition-all"
                    >
                      Create Team
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateTeam(false);
                        setTeamName('');
                      }}
                      className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateTeam(true)}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition-all"
                >
                  Get Started
                </button>
              )}
            </div>

            {/* Join Team */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4">
                  <UserPlus className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Join a Team</h2>
                <p className="text-gray-300">Enter an invite code to join</p>
              </div>
              
              {showJoinTeam ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value)}
                    placeholder="Enter invite code..."
                    className="w-full bg-white/10 text-white placeholder-gray-400 px-4 py-3 rounded-lg border border-white/20 focus:outline-none focus:border-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleJoinTeam()}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleJoinTeam}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-all"
                    >
                      Join Team
                    </button>
                    <button
                      onClick={() => {
                        setShowJoinTeam(false);
                        setJoinCode('');
                      }}
                      className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowJoinTeam(true)}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg transition-all"
                >
                  Join Now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main app UI
  const isManager = isOrgManager;
  const dailyHours = selectedMember ? calculateTotalTime(selectedMember.id, 'daily') : 0;
  const weeklyHours = selectedMember ? calculateTotalTime(selectedMember.id, 'weekly') : 0;
  const monthlyHours = selectedMember ? calculateTotalTime(selectedMember.id, 'monthly') : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">{currentOrg?.name || 'Team Time Tracker'}</h1>
              <p className="text-slate-400">
                {isManager ? 'Professional time management for your team' : 'Track your time'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={backToTeams}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-300 hover:bg-slate-700 rounded-xl transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
              {userOrganizations.length > 1 ? 'My Teams' : 'Back'}
            </button>
            
            {isManager ? (
              <>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-300 hover:bg-green-500/30 rounded-xl transition-all duration-300 border border-green-500/30"
                >
                  <Mail className="w-5 h-5" />
                  Invite
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 text-slate-300 hover:bg-slate-700 rounded-xl transition-all duration-300"
                >
                  <Settings className="w-5 h-5" />
                  Settings
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-lg">
                <UserAvatar
                  name={currentUser.displayName || currentUser.email}
                  photoURL={currentUser.photoURL}
                />
                <div className="min-w-0 text-left">
                  <div className="truncate font-medium text-white">{currentUser.displayName}</div>
                  <div className="truncate text-xs text-gray-300">{currentUser.email}</div>
                </div>
              </div>
            )}
            
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-300 hover:bg-red-500/30 rounded-xl transition-all duration-300 border border-red-500/30"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-4">Invite Team Members</h2>
              <p className="text-gray-300 mb-6">Share this invite code with your team:</p>
              
              <div className="bg-white/10 rounded-lg p-4 mb-6 border border-white/20">
                <div className="flex items-center justify-between">
                  <code className="text-2xl font-mono text-white">{currentOrg?.inviteCode}</code>
                  <button
                    onClick={copyInviteCode}
                    className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-all"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setShowInviteModal(false)}
                className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Settings Modal (Manager Only) */}
        {showSettings && isManager && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800 z-10">
                <div className="flex items-center gap-3">
                  <Settings className="w-6 h-6 text-blue-400" />
                  <h2 className="text-2xl font-bold text-white">Employee Management</h2>
                </div>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    handleLogout();
                  }}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-all duration-300"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="p-6">
                {!isAuthenticated ? (
                  <div className="text-center py-8">
                    <div className="inline-flex p-4 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-2xl mb-4">
                      <Lock className="w-12 h-12 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Protected Area</h3>
                    <p className="text-slate-400 mb-6">Enter password to manage employees</p>
                    <div className="max-w-sm mx-auto">
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                        placeholder="Enter password"
                        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-all duration-300 mb-4"
                      />
                      <button
                        onClick={handleLogin}
                        className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300"
                      >
                        Unlock
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Add New Employee */}
                    <div className="mb-6 p-4 bg-slate-700/30 rounded-xl border border-slate-600/50">
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-green-400" />
                        Add New Employee
                      </h3>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newEmployeeName}
                          onChange={(e) => setNewEmployeeName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addEmployee()}
                          placeholder="Employee name"
                          className="flex-1 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-all duration-300"
                        />
                        <button
                          onClick={addEmployee}
                          className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300"
                        >
                          Add
                        </button>
                      </div>
                    </div>

                    {/* Employee List */}
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-400" />
                        Current Employees ({teamMembers.length})
                      </h3>
                      <div className="space-y-2">
                        {teamMembers.map((member) => (
                          <div
                            key={member.id}
                            className="p-4 bg-slate-700/30 rounded-xl border border-slate-600/50 hover:border-slate-500 transition-all duration-300"
                          >
                            {editingId === member.id ? (
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${member.color}`}></div>
                                <input
                                  type="text"
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                                  className="flex-1 px-3 py-1 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-all duration-300"
                                  autoFocus
                                />
                                <button
                                  onClick={saveEdit}
                                  className="p-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-all duration-300"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="p-2 bg-slate-600/50 text-slate-400 hover:bg-slate-600 rounded-lg transition-all duration-300"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${member.color}`}></div>
                                  <span className="text-white font-medium">{member.name}</span>
                                  <span className="text-xs text-slate-400">
                                    {formatTime(getTwoMonthTotal(member.id))} total
                                  </span>
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => startEditing(member)}
                                    className="p-2 hover:bg-slate-600/50 rounded-lg transition-all duration-300"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4 text-blue-400" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      deleteEmployee(member.id);
                                    }}
                                    className="p-2 hover:bg-slate-600/50 rounded-lg transition-all duration-300"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-slate-700">
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2 bg-slate-700/50 text-slate-300 hover:bg-slate-700 rounded-lg transition-all duration-300"
                      >
                        Lock Settings
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* MANAGER VIEW */}
        {isManager && (
          <>
            {/* Team Member Selection */}
            <div className="mb-8 bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-semibold text-white">Select Team Member</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {teamMembers.map(member => (
                  <button
                    key={member.id}
                    onClick={() => setSelectedMember(member)}
                    className={`p-4 rounded-xl transition-all duration-300 ${
                      selectedMember?.id === member.id
                        ? `bg-gradient-to-r ${member.color} text-white shadow-lg scale-105`
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700 hover:scale-102'
                    } cursor-pointer`}
                  >
                    <div className="font-medium text-center">{member.name}</div>
                    <div className="text-xs mt-1 text-center opacity-80">
                      {formatTime(getTwoMonthTotal(member.id))}
                    </div>
                  </button>
                ))}
              </div>
            </div>

              {/* Quick Stats - Manager doesn't have timer */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/30 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Today</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">{formatTime(dailyHours)}</div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Total This Week</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">{formatTime(weeklyHours)}</div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-6 border border-emerald-500/30 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">Total This Month</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">{formatTime(monthlyHours)}</div>
                </div>
              </div>
          </>
        )}

        {/* MEMBER VIEW */}
        {!isManager && selectedMember && (
          <>
            {/* Timer Display */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-xl">
                <div className="text-center mb-8">
                  <div className={`inline-block px-6 py-2 rounded-full bg-gradient-to-r ${selectedMember.color} mb-4`}>
                    <span className="text-white font-medium">{selectedMember.name}</span>
                  </div>
                  <div className="text-7xl font-bold text-white mb-8 font-mono tracking-wider">
                    {formatTime(elapsedTime)}
                  </div>
                  
                  <div className="flex gap-4 justify-center">
                    {!isTracking ? (
                      <button
                        onClick={startTracking}
                        className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-green-500/50 hover:scale-105 transition-all duration-300"
                      >
                        <Play className="w-6 h-6" />
                        Start Timer
                      </button>
                    ) : (
                      <button
                        onClick={stopTracking}
                        className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-red-500/50 hover:scale-105 transition-all duration-300 animate-pulse"
                      >
                        <Square className="w-6 h-6" />
                        Stop Timer
                      </button>
                    )}
                  </div>
                </div>
              </div>

                {/* Quick Stats */}
                <div className="space-y-4">
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/30 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Today</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">{formatTime(dailyHours)}</div>
                </div>

                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/30 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">Total This Week</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">{formatTime(weeklyHours)}</div>
                </div>

                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-6 border border-emerald-500/30 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">Total This Month</h3>
                  </div>
                  <div className="text-3xl font-bold text-white">{formatTime(monthlyHours)}</div>
                </div>
                </div>
            </div>
          </>
        )}

        {/* Time summary — managers see all members; members see only their own */}
        {selectedMember && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
            <div className="mb-6 space-y-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-2xl font-bold text-white">
                  {isManager ? 'Time Summary' : 'Your Time Summary'}
                </h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowCalendar(!showCalendar)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                      showCalendar
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <CalendarDays className="w-4 h-4" />
                    Calendar
                  </button>
                  {(['daily', 'weekly', 'monthly'] as const).map(v => (
                    <button
                      key={v}
                      onClick={() => { setView(v); setShowCalendar(false); }}
                      className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                        view === v && !showCalendar
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                          : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => (showCalendar ? changeMonth(-1) : navigateDate(-1))}
                    className="p-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-700 transition-all duration-300"
                    title={showCalendar ? 'Previous month' : `Previous ${view.slice(0, -2)}`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="min-w-[12rem] px-2 text-center text-lg font-semibold text-white sm:min-w-[16rem]">
                    {showCalendar
                      ? `${monthNames[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`
                      : getCurrentPeriodLabel()}
                  </span>
                  <button
                    onClick={() => (showCalendar ? changeMonth(1) : navigateDate(1))}
                    className="p-2 rounded-lg bg-slate-700/50 text-slate-300 hover:bg-slate-700 transition-all duration-300"
                    title={showCalendar ? 'Next month' : `Next ${view.slice(0, -2)}`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <button
                  onClick={resetToCurrentPeriod}
                  className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-all duration-300 text-sm font-medium"
                  title={showCalendar ? 'Go to current month' : 'Go to current period'}
                >
                  Today
                </button>
              </div>
            </div>

            {showCalendar ? (
              <div>
                {/* Team Member Filter (Manager Only) */}
                {isManager && (
                  <div className="mb-6 flex flex-wrap gap-2">
                    {teamMembers.map(member => (
                      <button
                        key={member.id}
                        onClick={() => setSelectedMember(member)}
                        className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                          selectedMember?.id === member.id
                            ? `bg-gradient-to-r ${member.color} text-white shadow-lg`
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        {member.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Calendar Grid */}
                <div className="overflow-visible rounded-xl border border-slate-600/50 bg-slate-700/30 p-4">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-2 mb-2">
                    {dayNames.map(day => (
                      <div key={day} className="text-center text-sm font-semibold text-slate-400 py-2">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Days */}
                  <div className="grid grid-cols-7 gap-2 overflow-visible">
                    {(() => {
                      const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(calendarMonth);
                      const days = [];
                      
                      for (let i = 0; i < startingDayOfWeek; i++) {
                        days.push(<div key={`empty-${i}`} className="aspect-square" />);
                      }
                      
                      for (let day = 1; day <= daysInMonth; day++) {
                        const date = new Date(year, month, day);
                        const sessions = getSessionsForDate(selectedMember.id, date);
                        const time = sessions.reduce((sum, session) => sum + session.duration, 0);
                        const hours = (time / (1000 * 60 * 60)).toFixed(1);
                        const isToday = date.toDateString() === new Date().toDateString();
                        
                        days.push(
                          <div
                            key={day}
                            className={`group relative aspect-square rounded-lg p-2 transition-all duration-300 ${
                              isToday
                                ? 'bg-gradient-to-br from-blue-500/30 to-purple-600/30 border-2 border-blue-500'
                                : time > 0
                                ? 'bg-slate-600/50 hover:bg-slate-600 cursor-default'
                                : 'bg-slate-700/30 hover:bg-slate-700/50'
                            }`}
                          >
                            <div className="text-sm font-medium text-white mb-1">{day}</div>
                            {time > 0 && (
                              <div className={`text-xs font-semibold bg-gradient-to-r ${selectedMember.color} bg-clip-text text-transparent`}>
                                {hours}h
                              </div>
                            )}

                            {sessions.length > 0 && (
                              <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden w-max max-w-[240px] -translate-x-1/2 rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-left shadow-2xl group-hover:block">
                                <p className="mb-2 border-b border-slate-700 pb-1.5 text-xs font-semibold text-white">
                                  {date.toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </p>
                                <div className="space-y-1.5">
                                  {sessions.map((session, index) => (
                                    <div key={`${session.startTime}-${index}`} className="text-xs leading-relaxed">
                                      <span className="text-green-400">{formatClockTime(session.startTime)}</span>
                                      <span className="text-slate-500"> → </span>
                                      <span className="text-red-400">{formatClockTime(session.endTime)}</span>
                                      <span className="ml-1 text-slate-400">
                                        ({formatSessionDuration(session.duration)})
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <p className="mt-2 border-t border-slate-700 pt-1.5 text-xs text-slate-400">
                                  Total: {formatTime(time)}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return days;
                    })()}
                  </div>

                  {/* Calendar Legend */}
                  <div className="mt-4 pt-4 border-t border-slate-600/50">
                    <div className="text-sm text-slate-400">
                      Total for {monthNames[calendarMonth.getMonth()]}: 
                      <span className="ml-2 font-semibold text-white">
                        {(() => {
                          const { daysInMonth, year, month } = getDaysInMonth(calendarMonth);
                          let total = 0;
                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(year, month, day);
                            total += getTimeForDate(selectedMember.id, date);
                          }
                          return formatTime(total);
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : isManager ? (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {teamMembers.map(member => {
                  const time = calculateTotalTime(member.id, view);
                  const hours = (time / (1000 * 60 * 60)).toFixed(2);

                  return (
                    <div
                      key={member.id}
                      className="bg-slate-700/30 rounded-xl p-4 border border-slate-600/50 hover:border-slate-500 transition-all duration-300"
                    >
                      <div className={`text-sm font-medium mb-2 bg-gradient-to-r ${member.color} bg-clip-text text-transparent`}>
                        {member.name}
                      </div>
                      <div className="text-2xl font-bold text-white mb-1">
                        {formatTime(time)}
                      </div>
                      <div className="text-xs text-slate-400">{hours} hours</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-600/50 bg-gradient-to-br from-slate-700/40 to-slate-800/40 p-8 text-center">
                <div className={`mx-auto mb-4 inline-flex rounded-full bg-gradient-to-r px-4 py-1.5 ${selectedMember.color}`}>
                  <span className="text-sm font-medium text-white">{selectedMember.name}</span>
                </div>
                <p className="mb-2 text-sm text-slate-400">{getCurrentPeriodLabel()}</p>
                <div className="font-mono text-5xl font-bold tracking-wide text-white sm:text-6xl">
                  {formatTime(calculateTotalTime(selectedMember.id, view))}
                </div>
                <p className="mt-3 text-slate-400">
                  {(calculateTotalTime(selectedMember.id, view) / (1000 * 60 * 60)).toFixed(2)} hours
                  <span className="mx-2 text-slate-600">·</span>
                  <span className="capitalize">{view}</span> total
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamTimeTrackerFinal;

